import React, { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";

const appleEntrance = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(235, 235, 245, 0.3);
  backdrop-filter: blur(10px) saturate(180%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #E5E5E7;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${appleEntrance} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 40px 80px rgba(0,0,0,0.08);
`;

const Header = styled.header`
  padding: 24px;
  border-bottom: 1px solid #F2F2F7;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1D1D1F;
  }
`;

const CloseButton = styled.button`
  background: #F5F5F7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #86868B;

  &:hover {
    background: #E5E5E7;
    color: #1D1D1F;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
`;

const AppleInput = styled.input`
  flex: 1;
  height: 40px;
  background: #F5F5F7;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0 14px;
  font-size: 14px;

  &:focus {
    outline: none;
    background: #fff;
    border-color: #FF8F40;
    box-shadow: 0 0 0 4px rgba(255,143,64,.15);
  }
`;

const AppleSelect = styled.select`
  height: 40px;
  width: 140px;
  background: #F5F5F7;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Panel = styled.div`
  border: 1px solid #F2F2F7;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  background: #F5F5F7;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    font-size: 13px;
    font-weight: 600;
  }
`;

const PanelContent = styled.div`
  padding: 16px;
  background: #FFFFFF;
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

const ConfigItem = styled.div`
  background: #F5F5F7;
  padding: 12px 14px;
  border-radius: 8px;

  span:first-child {
    font-size: 11px;
    color: #86868B;
  }

  span:last-child {
    font-size: 13px;
    font-weight: 600;
    word-break: break-all;
  }
`;

const DocCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #F2F2F7;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;

  .label {
    font-size: 11px;
    font-weight: 700;
    color: #FF8F40;
    margin-bottom: 8px;
    display: block;
  }

  p {
    font-size: 14px;
    white-space: pre-wrap;
  }

  mark {
    background: rgba(255,143,64,.25);
    border-radius: 2px;
  }
`;

const DocsModal = ({ open, data, onClose }) => {
  const [activePanel, setActivePanel] = useState("engine");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("default");

  const togglePanel = (key) =>
    setActivePanel(activePanel === key ? null : key);

  const highlight = (text) => {
    if (!search) return text;
    return text.split(new RegExp(`(${search})`, "gi")).map((p, i) =>
      p.toLowerCase() === search.toLowerCase() ? <mark key={i}>{p}</mark> : p
    );
  };

  const engineConfigs = useMemo(() => {
    if (!data) return [];
    const ignore = ["docs", "prompt", "promptuser", "query"];
    return Object.entries(data)
      .filter(([k, v]) =>
        !ignore.includes(k) &&
        String(v).toLowerCase().includes(search.toLowerCase())
      )
      .map(([k, v]) => ({ key: k, value: String(v) }));
  }, [data, search]);

  const docs = useMemo(() => {
    let list = (data?.docs || []).filter(d =>
      d.toLowerCase().includes(search.toLowerCase())
    );
    if (sortOrder === "alpha") list.sort();
    return list;
  }, [data, search, sortOrder]);

  const prompts = useMemo(() => {
    const list = [
      { label: "Prompt do Sistema", value: data?.prompt },
      { label: "Prompt do Usuário", value: data?.promptuser },
      { label: "Query de Avaliação", value: data?.query },
    ].filter(p =>
      p.value?.toLowerCase().includes(search.toLowerCase())
    );

    if (sortOrder === "alpha")
      list.sort((a, b) => a.label.localeCompare(b.label));

    return list;
  }, [data, search, sortOrder]);

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <TitleRow>
            <h2>Contexto do Sistema</h2>
            <CloseButton onClick={onClose}>✕</CloseButton>
          </TitleRow>

          <Toolbar>
            <AppleInput
              placeholder="Buscar em configs, prompts e documentos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <AppleSelect value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="default">Original</option>
              <option value="alpha">A–Z</option>
            </AppleSelect>
          </Toolbar>
        </Header>

        <ScrollArea>

          <Panel>
            <PanelHeader onClick={() => togglePanel("engine")}>
              <span>Configurações da Engine</span>
              <span>{activePanel === "engine" ? "−" : "+"}</span>
            </PanelHeader>
            {activePanel === "engine" && (
              <PanelContent>
                <ConfigGrid>
                  {engineConfigs.map((c, i) => (
                    <ConfigItem key={i}>
                      <span>{c.key}</span> <br />
                      <span>{highlight(c.value)}</span>
                    </ConfigItem>
                  ))}
                </ConfigGrid>
              </PanelContent>
            )}
          </Panel>

          <Panel>
            <PanelHeader onClick={() => togglePanel("prompts")}>
              <span>Prompts Utilizados</span>
              <span>{activePanel === "prompts" ? "−" : "+"}</span>
            </PanelHeader>
            {activePanel === "prompts" && (
              <PanelContent>
                {prompts.map((p, i) => (
                  <DocCard key={i}>
                    <span className="label">{p.label}</span>
                    <p>{highlight(p.value || "")}</p>
                  </DocCard>
                ))}
              </PanelContent>
            )}
          </Panel>

          <Panel>
            <PanelHeader onClick={() => togglePanel("docs")}>
              <span>Documentos Utilizados ({docs.length})</span>
              <span>{activePanel === "docs" ? "−" : "+"}</span>
            </PanelHeader>
            {activePanel === "docs" && (
              <PanelContent>
                {docs.map((d, i) => (
                  <DocCard key={i}>
                    <span className="label">Documento #{i + 1}</span>
                    <p>{highlight(d)}</p>
                  </DocCard>
                ))}
              </PanelContent>
            )}
          </Panel>

        </ScrollArea>
      </ModalContainer>
    </Overlay>
  );
};

export default DocsModal;
